import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { NavController } from '@ionic/angular';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { RecommendationQuestionsNavigationState } from 'src/app/models/recommendation/recommendation-questions-navigation-state';
import { ReturnRecommendationQuestion } from 'src/app/models/recommendation/return-recommendation-question';
import { ReturnRecommendationQuestionsPayload } from 'src/app/models/recommendation/return-recommendation-questions-payload';
import { ReturnMessage } from 'src/app/models/return-message';
import { LoadingControllerWrapperService } from 'src/app/services/loading-controller-wrapper/loading-controller-wrapper.service';
import { PermissionCheckerService } from 'src/app/services/permission-checker/permission-checker.service';
import { RecommendationService } from 'src/app/services/recommendation/recommendation.service';
import { ToastControllerWrapperService } from 'src/app/services/toast-controller-wrapper/toast-controller-wrapper.service';

@Component({
  selector: 'app-recommendation-questions',
  templateUrl: './recommendation-questions.page.html',
  styleUrls: ['./recommendation-questions.page.scss'],
})
export class RecommendationQuestionsPage implements OnInit {

  title: string = 'Recommendation Questions';
  questions: ReturnRecommendationQuestion[] = null;
  disableSubmitButton: boolean;
  canLeave: boolean;
  canCRUDRecommendations: boolean;
  form: FormGroup = new FormGroup({});
  navigationState: RecommendationQuestionsNavigationState;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private navController: NavController,
    private titleService: Title,
    private loadingWrapper: LoadingControllerWrapperService,
    private toastWrapper: ToastControllerWrapperService,
    private permissionChecker: PermissionCheckerService,
    private recommendationService: RecommendationService
  ) { }

  ngOnInit() {
    this.navigationState = <RecommendationQuestionsNavigationState> this.router.getCurrentNavigation()?.extras.state;
    this.disableSubmitButton = true;
    this.canLeave = false;
  }

  async ionViewWillEnter() {
    this.titleService.setTitle(this.title);
    if (!this.navigationState) {
      this.navController.navigateRoot(['']);
      return;
    }

    await this.loadingWrapper.presentLoading('');
    forkJoin([
      this.permissionChecker.canCRUDRecommendations(),
      this.recommendationService.getRecommendationQuestions(this.navigationState.recommendationId)
    ])
      .pipe(finalize(async () => await this.loadingWrapper.dismissLoading()))
      .subscribe(([canCRUDRecommendations, getRecommendationQuestions]: [boolean, ReturnMessage<ReturnRecommendationQuestionsPayload>]) => {
        this.canCRUDRecommendations = canCRUDRecommendations;
        this.questions = getRecommendationQuestions.success ? getRecommendationQuestions.payload.questions : null;

        if (!this.questions || this.questions.length === 0) {
          this.disableSubmitButton = true;
          this.canLeave = true;
        } else {
          // Build the form dynamically based on the existing list of questions.
          const group: any = {};
          for (const question of this.questions) {
            group[question.id] = new FormControl(question.value || '');
          }
          this.form = new FormGroup(group);

          this.disableSubmitButton = false;
          this.canLeave = false;
        }
      });
  }

  async submit() {
    if (this.form.valid) {
      this.canLeave = true;

      await this.toastWrapper.dismissToast();
      await this.loadingWrapper.presentLoading('');

      let post = {
        questionsAnswers: [],
      };

      for (const key in this.form.value) {
        post.questionsAnswers.push({ id: key, value: this.form.value[key] });
      }

      this.recommendationService
        .updateRecommendationQuestions(post)
        .pipe(
          finalize(async () => {
            await this.loadingWrapper.dismissLoading();
          })
        )
        .subscribe((response) => {
          if (response.success) {
            this.router.navigate(['..'], { relativeTo: this.route });
          }
        });
    } else {
      await this.toastWrapper.dismissToast();
      await this.toastWrapper.presentErrorToast('Please complete the form');
    }
  }
}
